import React, { createContext, useState, useMemo, useCallback } from 'react'

import { Modifier, Obj, Placement, Rect } from '@popperjs/core'
import { usePopper } from 'react-popper'
import { ModifierArguments } from '@popperjs/core'
import { useResizeObserver } from './useResizeObserver'

type PopperReferenceType = HTMLElement | null
type PopperType = HTMLElement | null
type ArrowType = HTMLElement | null

/** @deprecated - See {@link Popover} */
export type PopperStyles = {
  attributes: { [key: string]: { [key: string]: string } | undefined }
  styles: { [key: string]: React.CSSProperties }
}

interface TriggerCtx {
  popperStyles: PopperStyles
  setArrowElement: React.Dispatch<ArrowType>
  setPopperElement: React.Dispatch<PopperType>
  setTriggerElem: React.Dispatch<PopperReferenceType>
  triggerElem: PopperReferenceType
}

/** @deprecated - See {@link Popover} */
export const popperContext = createContext<TriggerCtx>({
  triggerElem: null,
  setTriggerElem: () => null,
  setPopperElement: () => null,
  setArrowElement: () => null,
  popperStyles: { attributes: {}, styles: {} },
})

type OffsetsFunction = (args: {
  placement: Placement
  popper: Rect
  reference: Rect
}) => [number, number]

/** @deprecated - See {@link Popover} */
export type PopoverProps = {
  children: React.ReactNode
  isFlipEnabled?: boolean
  offset?: OffsetsFunction | [number, number]
  placement?: Placement
  preventOverflowAltAxis?: boolean
  repositionOnResize?: boolean
  sameWidth?: boolean
  strategy?: 'absolute' | 'fixed'
  zIndex?: number
}

/** @deprecated Use Popover from @chilipiper/design-system/src/new instead */
export const Popover = ({
  children,
  placement,
  offset,
  zIndex,
  sameWidth = false,
  preventOverflowAltAxis = true,
  isFlipEnabled = false,
  repositionOnResize = false,
  strategy,
}: PopoverProps) => {
  const [triggerElem, setTriggerElem] = useState<PopperReferenceType>(null)
  const [popperElement, setPopperElement] = useState<PopperType>(null)
  const [arrowElement, setArrowElement] = useState<ArrowType>(null)

  const modifiers = useMemo(() => {
    return [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'preventOverflow',
        options: {
          altAxis: preventOverflowAltAxis,
        },
      },
      {
        name: 'flip',
        enabled: isFlipEnabled,
      },
      {
        name: 'offset',
        options: {
          offset: offset,
        },
      },
      {
        name: 'sameWidth',
        enabled: sameWidth,
        fn: ({ state }: ModifierArguments<any>) => {
          state.styles.popper.width = `${state.rects.reference.width}px`
        },
        effect({ state }) {
          state.elements.popper.style.width = `${
            (state.elements.reference as HTMLElement).offsetWidth
          }px`
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
    ] as Modifier<string, Obj>[]
  }, [offset, arrowElement, isFlipEnabled, preventOverflowAltAxis, sameWidth])

  const { styles, attributes, update } = usePopper(triggerElem, popperElement, {
    modifiers,
    placement,
    strategy,
  })

  const onResize = useCallback(() => {
    if (update) {
      update()
    }
  }, [update])

  useResizeObserver(popperElement, onResize, repositionOnResize)

  return (
    <popperContext.Provider
      value={{
        setTriggerElem,
        triggerElem,
        setPopperElement,
        setArrowElement,
        popperStyles: {
          styles: {
            ...styles,
            popper: {
              ...styles.popper,
              zIndex,
            },
          },
          attributes,
        },
      }}
    >
      {children}
    </popperContext.Provider>
  )
}
